import React from 'react'
import {
  BarChart3,
  Users,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Activity,
  Target,
  Zap,
  Shield,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Server,
  Globe,
  Gauge
} from 'lucide-react'

// 统计卡片组件
const StatCard: React.FC<{
  title: string
  value: string
  change: string
  trend: 'up' | 'down'
  icon: React.ReactNode
  color: string
}> = ({ title, value, change, trend, icon, color }) => (
  <div className="group bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-xl hover:shadow-slate-100/50 hover:border-slate-200 transition-all">
    <div className="flex items-start justify-between mb-4">
      <div
        className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-lg`}
      >
        {icon}
      </div>
      <div
        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
          trend === 'up'
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-rose-50 text-rose-700 border border-rose-200'
        }`}
      >
        {trend === 'up' ? (
          <ArrowUpRight className="w-3.5 h-3.5" />
        ) : (
          <ArrowDownRight className="w-3.5 h-3.5" />
        )}
        {change}
      </div>
    </div>
    <div className="text-3xl font-bold text-slate-900 mb-1">{value}</div>
    <div className="text-sm text-slate-500">{title}</div>
  </div>
)

// 健康度指标组件
const HealthIndicator: React.FC<{
  title: string
  status: 'excellent' | 'good' | 'warning' | 'danger'
  subtitle?: string
}> = ({ title, status, subtitle }) => {
  const statusConfig = {
    excellent: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      icon: <CheckCircle className="h-5 w-5 text-emerald-500" />,
      text: 'text-emerald-700',
      label: '优秀'
    },
    good: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: <CheckCircle className="h-5 w-5 text-blue-500" />,
      text: 'text-blue-700',
      label: '良好'
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: <AlertCircle className="h-5 w-5 text-amber-500" />,
      text: 'text-amber-700',
      label: '关注'
    },
    danger: {
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      icon: <AlertCircle className="h-5 w-5 text-rose-500" />,
      text: 'text-rose-700',
      label: '需改善'
    }
  }

  const config = statusConfig[status]

  return (
    <div
      className={`flex items-center justify-between p-4 ${config.bg} rounded-xl border ${config.border} transition-all hover:shadow-md`}
    >
      <div className="flex items-center gap-3">
        {config.icon}
        <div>
          <span className="font-semibold text-slate-800">{title}</span>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <span className={`font-bold text-sm ${config.text}`}>{config.label}</span>
    </div>
  )
}

export const AdminPlatformAnalysis: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAzIi8+PC9nPjwvc3ZnPg==')] opacity-50" />
        <div className="relative px-8 py-10">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-xl">
              <BarChart3 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">平台数据分析</h2>
              <p className="text-slate-400">AI 全面分析平台数据，洞察业务健康度</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Sparkles className="w-4 h-4 text-indigo-400" />
            <span>数据更新于 2 分钟前</span>
          </div>
        </div>
      </div>

      {/* 核心指标 */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="总用户数"
          value="2,845"
          change="+12.5%"
          trend="up"
          icon={<Users className="w-6 h-6" />}
          color="from-blue-400 to-indigo-500"
        />
        <StatCard
          title="活跃教师"
          value="186"
          change="+8.3%"
          trend="up"
          icon={<Users className="w-6 h-6" />}
          color="from-emerald-400 to-teal-500"
        />
        <StatCard
          title="月收入"
          value="¥156K"
          change="+18.2%"
          trend="up"
          icon={<DollarSign className="w-6 h-6" />}
          color="from-purple-400 to-pink-500"
        />
        <StatCard
          title="课程完成率"
          value="94.2%"
          change="+2.1%"
          trend="up"
          icon={<BarChart3 className="w-6 h-6" />}
          color="from-amber-400 to-orange-500"
        />
      </div>

      {/* 平台健康度 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white shadow-lg">
            <Activity className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">平台健康度指标</h3>
            <p className="text-sm text-slate-500">实时监控各项核心指标状态</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-4">
            <HealthIndicator title="用户活跃度" status="excellent" subtitle="日均活跃 1,200+" />
            <HealthIndicator title="收入增长" status="good" subtitle="环比增长 18.2%" />
            <HealthIndicator title="教师流失率" status="warning" subtitle="本月流失 3 人" />
          </div>
          <div className="space-y-4">
            <HealthIndicator title="课程质量" status="excellent" subtitle="平均评分 4.8" />
            <HealthIndicator title="支付成功率" status="good" subtitle="成功率 99.2%" />
            <HealthIndicator title="投诉处理" status="danger" subtitle="待处理 5 件" />
          </div>
        </div>
      </div>
    </div>
  )
}

export const AdminOperationRecommendations: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Target className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">AI 运营建议</h2>
          <p className="text-white/80">基于数据分析提供精准的运营策略建议</p>
        </div>
      </div>

      {/* 建议列表 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-8">
        <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-500" />
          重点建议
        </h3>
        <div className="space-y-4">
          <div className="group p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 hover:shadow-lg hover:shadow-blue-100/50 transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-blue-800 mb-1">用户增长策略</div>
                <div className="text-sm text-blue-600">
                  增加编程类课程推广，该类目需求增长 25%，建议开设更多相关课程
                </div>
              </div>
            </div>
          </div>
          <div className="group p-5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100 hover:shadow-lg hover:shadow-emerald-100/50 transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-emerald-800 mb-1">收入优化建议</div>
                <div className="text-sm text-emerald-600">
                  调整高端课程定价策略，数据显示用户对精品课程价格敏感度较低
                </div>
              </div>
            </div>
          </div>
          <div className="group p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-100 hover:shadow-lg hover:shadow-amber-100/50 transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-amber-800 mb-1">教师留存策略</div>
                <div className="text-sm text-amber-600">
                  建议推出教师激励计划，降低流失率，提高平台教学质量
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const AdminUserAnalysis: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">用户行为分析</h2>
          <p className="text-white/80">深度分析用户行为，提升用户体验和留存</p>
        </div>
      </div>

      {/* 数据卡片 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-8">
        <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-500" />
          用户活跃度分析
        </h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
            <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              2,845
            </div>
            <div className="text-sm text-slate-600 font-medium">总注册用户</div>
            <div className="mt-2 text-xs text-emerald-600 font-medium">↑ 12.5% 本月新增</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
            <div className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
              1,632
            </div>
            <div className="text-sm text-slate-600 font-medium">月活跃用户</div>
            <div className="mt-2 text-xs text-emerald-600 font-medium">活跃率 57.4%</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
            <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              89.2%
            </div>
            <div className="text-sm text-slate-600 font-medium">用户留存率</div>
            <div className="mt-2 text-xs text-emerald-600 font-medium">↑ 3.1% vs 上月</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const AdminRevenueAnalysis: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <DollarSign className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">收入分析</h2>
          <p className="text-white/80">收入趋势分析，发现增长机会</p>
        </div>
      </div>

      {/* 收入数据 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-8">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="text-center p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
              <DollarSign className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold text-emerald-700 mb-1">¥156K</div>
            <div className="text-sm text-slate-600 font-medium">本月收入</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold text-blue-700 mb-1">+18.2%</div>
            <div className="text-sm text-slate-600 font-medium">环比增长</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
              <Target className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold text-purple-700 mb-1">¥1.8M</div>
            <div className="text-sm text-slate-600 font-medium">年收入预测</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const AdminRiskAnalysis: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-500 via-red-500 to-orange-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">风险预警</h2>
          <p className="text-white/80">智能识别潜在风险，提前预警</p>
        </div>
      </div>

      {/* 风险列表 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-8">
        <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-rose-500" />
          风险预警清单
        </h3>
        <div className="space-y-4">
          <div className="p-5 bg-gradient-to-r from-rose-50 to-red-50 rounded-2xl border border-rose-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-rose-500 rounded-full animate-pulse" />
                <span className="font-bold text-rose-800">高风险</span>
              </div>
              <span className="text-xs text-rose-600 bg-rose-100 px-2 py-1 rounded-full">
                需立即处理
              </span>
            </div>
            <div className="text-sm text-rose-700">
              教师流失率上升，本月已有 3 位教师离开平台，建议启动教师关怀计划
            </div>
          </div>
          <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-amber-500 rounded-full" />
                <span className="font-bold text-amber-800">中风险</span>
              </div>
              <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                建议关注
              </span>
            </div>
            <div className="text-sm text-amber-700">
              投诉处理时效下降，平均处理时长从 24 小时增至 48 小时，建议增加客服人力
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const AdminSystemOptimization: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-50" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Server className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">系统优化</h2>
          <p className="text-slate-400">系统性能分析和优化建议</p>
        </div>
      </div>

      {/* 系统指标 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-8">
        <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
          <Gauge className="w-5 h-5 text-cyan-500" />
          系统性能指标
        </h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="text-center p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
              <Clock className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold text-emerald-700 mb-1">1.2s</div>
            <div className="text-sm text-slate-600 font-medium">页面加载时间</div>
            <div className="mt-2 text-xs text-emerald-600">优秀</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
              <Globe className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold text-blue-700 mb-1">99.5%</div>
            <div className="text-sm text-slate-600 font-medium">系统稳定性</div>
            <div className="mt-2 text-xs text-emerald-600">正常</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
              <Zap className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold text-purple-700 mb-1">98.2%</div>
            <div className="text-sm text-slate-600 font-medium">API 成功率</div>
            <div className="mt-2 text-xs text-emerald-600">健康</div>
          </div>
        </div>
      </div>
    </div>
  )
}
